import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { FaUser, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaEnvelope, FaPhone, FaIdCard, FaUserTag, FaLock } from 'react-icons/fa';
import './ChefsPage.css';

const ChefsPage = () => {
    const [chefs, setChefs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingChef, setEditingChef] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        chefId: '',
        specialization: '',
        experience: '',
        status: 'active',
        password: ''
    });

    useEffect(() => {
        fetchChefs();
    }, []);

    const fetchChefs = async () => {
        try {
            setLoading(true);
            const chefsRef = collection(db, 'chefs');
            const snapshot = await getDocs(chefsRef);
            const chefsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setChefs(chefsList);
        } catch (error) {
            console.error('Error fetching chefs:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateChefId = () => {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `CHEF${timestamp}${random}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.email || (!editingChef && !formData.password)) {
            alert('Please fill in all required fields (Name, Email, Password).');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }

        try {
            if (editingChef) {
                // --- UPDATE ---
                const chefDataToUpdate = {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    chefId: formData.chefId,
                    specialization: formData.specialization,
                    experience: formData.experience,
                    status: formData.status,
                    updatedAt: new Date()
                };

                if (formData.password) {
                    chefDataToUpdate.password = formData.password;
                }

                await updateDoc(doc(db, 'chefs', editingChef.id), chefDataToUpdate);
                setEditingChef(null);
            } else {
                // --- CREATE ---
                const newChefData = {
                    ...formData,
                    role: 'chef',
                    chefId: formData.chefId || generateChefId(),
                    createdAt: new Date(),
                    updatedAt: new Date(),
                };
                await addDoc(collection(db, 'chefs'), newChefData);
            }

            resetForm();
            fetchChefs();
        } catch (error) {
            // ✅ This is the corrected line, now inside the catch block
            console.error('Error saving chef:', error);
            alert('Error saving chef. Please try again.');
        }
    };

    const handleEdit = (chef) => {
        setEditingChef(chef);
        setFormData({
            name: chef.name,
            email: chef.email,
            phone: chef.phone || '',
            chefId: chef.chefId,
            specialization: chef.specialization || '',
            experience: chef.experience || '',
            status: chef.status || 'active',
            password: ''
        });
        setShowAddModal(true);
    };

    const handleDelete = async (chefId) => {
        if (window.confirm('Are you sure you want to delete this chef?')) {
            try {
                await deleteDoc(doc(db, 'chefs', chefId));
                fetchChefs();
            } catch (error) {
                console.error('Error deleting chef:', error);
                alert('Error deleting chef. Please try again.');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            chefId: '',
            specialization: '',
            experience: '',
            status: 'active',
            password: ''
        });
        setEditingChef(null);
        setShowAddModal(false);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return '#10b981';
            case 'inactive': return '#6b7280';
            case 'on_leave': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'Active';
            case 'inactive': return 'Inactive';
            case 'on_leave': return 'On Leave';
            default: return status;
        }
    };

    return (
        <div className="chefs-page">
            <div className="chefs-header">
                <h1>Chef Management</h1>
                <button className="add-chef-btn" onClick={() => setShowAddModal(true)}>
                    <FaPlus /> Add New Chef
                </button>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading chefs...</p>
                </div>
            ) : (
                <div className="chefs-grid">
                    {chefs.length === 0 ? (
                        <div className="no-chefs">
                            <FaUser size={48} />
                            <h3>No Chefs Found</h3>
                            <p>Add your first chef to get started</p>
                        </div>
                    ) : (
                        chefs.map((chef) => (
                            <div key={chef.id} className="chef-card">
                                <div className="chef-card-header">
                                    <div className="chef-info">
                                        <h3>{chef.name}</h3>
                                        <span className="status-badge" style={{ backgroundColor: getStatusColor(chef.status) }}>
                                            {getStatusText(chef.status)}
                                        </span>
                                    </div>
                                    <div className="chef-actions">
                                        <button className="edit-btn" onClick={() => handleEdit(chef)}><FaEdit /></button>
                                        <button className="delete-btn" onClick={() => handleDelete(chef.id)}><FaTrash /></button>
                                    </div>
                                </div>
                                <div className="chef-card-body">
                                    <div className="chef-details">
                                        <div className="detail-item"><FaIdCard className="detail-icon" /><span>{chef.chefId}</span></div>
                                        <div className="detail-item"><FaEnvelope className="detail-icon" /><span>{chef.email}</span></div>
                                        <div className="detail-item">
                                            <FaUserTag className="detail-icon" />
                                            <span className="detail-value" style={{ textTransform: 'capitalize' }}>{chef.role || 'N/A'}</span>
                                        </div>
                                        {chef.specialization && (
                                            <div className="detail-item">
                                                <span className="detail-label">Specialty:</span>
                                                <span className="detail-value">{chef.specialization}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingChef ? 'Edit Chef' : 'Add New Chef'}</h2>
                            <button className="modal-close-btn" onClick={resetForm}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="chef-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name">Full Name *</label>
                                        <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email">Email *</label>
                                        <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone Number</label>
                                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="chefId">Chef ID</label>
                                        <input type="text" id="chefId" name="chefId" value={formData.chefId} onChange={handleInputChange} placeholder="Auto-generated" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="password">
                                        <FaLock /> {editingChef ? 'New Password (Optional)' : 'Password *'}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required={!editingChef}
                                        className="form-control"
                                        placeholder={editingChef ? 'Leave blank to keep current' : 'Create a secure password'}
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="specialization">Specialization</label>
                                        <select id="specialization" name="specialization" value={formData.specialization} onChange={handleInputChange} className="form-control">
                                            <option value="">Select specialization</option>
                                            <option value="Indian Cuisine">Indian Cuisine</option>
                                            <option value="Italian Cuisine">Italian Cuisine</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="experience">Experience</label>
                                        <select id="experience" name="experience" value={formData.experience} onChange={handleInputChange} className="form-control">
                                            <option value="">Select experience level</option>
                                            <option value="0-2 years">0-2 years</option>
                                            <option value="3-5 years">3-5 years</option>
                                            <option value="6-10 years">6-10 years</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="status">Status</label>
                                    <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="form-control">
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="on_leave">On Leave</option>
                                    </select>
                                </div>
                                <div className="form-actions">
                                    <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
                                    <button type="submit" className="btn-primary"><FaSave /> {editingChef ? 'Update Chef' : 'Save Chef'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChefsPage;